import {
    AccountCategory,
    type AccountSummary,
    Provider,
} from '@fruitful/types';
import { useAtomValue } from 'jotai/index';
import React, { useEffect, useMemo, useState } from 'react';
import {
    BodySmallRegular,
    DollarInput,
    H6Emphasized,
    Input,
    LabelRegular,
    MultilineInput,
    Picker,
    ScrollView,
    Spinner,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import { Button } from '../../../common/components/GroveButtons';
import { GroveSimpleModal } from '../../../common/components/GroveSimpleModal';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../../common/components/Toasts';
import { selectedMembershipAtom } from '../../../entities/entityAtoms';
import {
    centsToDollarInputDisplayString,
    getAccountCategoryPickerOptions,
    GROVE_WIDE_MODAL_MAX_WIDTH_PX,
    parseDollarInputStringToCents,
} from '../../../entities/membership/components/accountModalFormUtils';
import { useUpdateManualAccountAndRefetch } from '../../../entities/membership/hooks/externalAccounts/useUpdateManualAccount';

export const MoneyMapperManualAccountDetailsModal = ({
    account,
    isOpen,
    onClose,
}: {
    account: AccountSummary;
    isOpen: boolean;
    onClose: () => void;
}) => {
    const membership = useAtomValue(selectedMembershipAtom);
    const toast = useToastController();
    const { updateManualAccountAsync } = useUpdateManualAccountAndRefetch(
        membership?.id ?? null,
    );
    const categoryOptions = useMemo(
        () => getAccountCategoryPickerOptions(),
        [],
    );

    const [name, setName] = useState(account.name ?? '');
    const [institutionName, setInstitutionName] = useState(
        account.institutionName ?? '',
    );
    const [selectedUserId, setSelectedUserId] = useState(
        account.userId ?? membership?.users[0]?.id ?? '',
    );
    const [holisticCategory, setHolisticCategory] = useState<AccountCategory>(
        account.category ?? AccountCategory.SAVE,
    );
    const [amountStr, setAmountStr] = useState(() =>
        centsToDollarInputDisplayString(account.currentBalanceCents ?? 0),
    );
    const [description, setDescription] = useState(account.description ?? '');
    const [minimumPaymentStr, setMinimumPaymentStr] = useState(() =>
        centsToDollarInputDisplayString(account.minimumPayment ?? 0),
    );
    const [interestRateStr, setInterestRateStr] = useState(
        account.interestRate != null && account.interestRate > 0
            ? String(account.interestRate)
            : '',
    );
    const [showSpinner, setShowSpinner] = useState(false);
    const [validationErrors, setValidationErrors] = useState<{
        name?: string;
        institutionName?: string;
    }>({});

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        setName(account.name ?? '');
        setInstitutionName(account.institutionName ?? '');
        setSelectedUserId(account.userId ?? membership?.users[0]?.id ?? '');
        setHolisticCategory(account.category ?? AccountCategory.SAVE);
        setAmountStr(
            centsToDollarInputDisplayString(account.currentBalanceCents ?? 0),
        );
        setDescription(account.description ?? '');
        setMinimumPaymentStr(
            centsToDollarInputDisplayString(account.minimumPayment ?? 0),
        );
        setInterestRateStr(
            account.interestRate != null && account.interestRate > 0
                ? String(account.interestRate)
                : '',
        );
        setValidationErrors({});
    }, [isOpen, account, membership?.users]);

    const ownerPickerOptions = useMemo(() => {
        if (membership == null) {
            return [];
        }
        return membership.users.map((u) => ({
            label: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim(),
            value: u.id,
        }));
    }, [membership]);

    if (account.provider !== Provider.MANUAL) {
        return null;
    }

    const parseInterestRate = (): number => {
        const t = interestRateStr.trim();
        if (t === '') {
            return 0;
        }
        const n = Number.parseFloat(t);
        return Number.isNaN(n) ? 0 : n;
    };

    const handleSave = async () => {
        const errors: typeof validationErrors = {};
        if (!name.trim()) {
            errors.name = 'Name is required.';
        }
        if (!institutionName.trim()) {
            errors.institutionName = 'Institution name is required.';
        }
        if (!selectedUserId) {
            toast.show(
                'Account holder is required',
                MutationErrorToastOptions(),
            );
            return;
        }
        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return;
        }

        const amountCents = parseDollarInputStringToCents(amountStr);
        if (amountCents == null) {
            toast.show('Enter a valid balance', MutationErrorToastOptions());
            return;
        }
        const minCents = parseDollarInputStringToCents(minimumPaymentStr);
        if (minCents == null) {
            toast.show(
                'Enter a valid minimum payment',
                MutationErrorToastOptions(),
            );
            return;
        }

        setShowSpinner(true);
        try {
            await updateManualAccountAsync({
                id: account.id,
                userId: selectedUserId,
                name: name.trim(),
                institutionName: institutionName.trim(),
                amount: amountCents,
                description: description ?? '',
                interestRate: parseInterestRate(),
                minimumPayment: minCents,
                holisticCategory: holisticCategory.toUpperCase(),
            });
            onClose();
            toast.show('Account saved', MutationSuccessToastOptions);
        } catch {
            toast.show('Error saving account', MutationErrorToastOptions());
        } finally {
            setShowSpinner(false);
        }
    };

    const inputsDisabled = showSpinner;

    return (
        <GroveSimpleModal
            isOpen={isOpen}
            onClose={onClose}
            hasCloseButton
            dialogProps={{
                width: GROVE_WIDE_MODAL_MAX_WIDTH_PX,
                maxWidth: '90vw',
            }}
        >
            <YStack
                width="100%"
                minWidth={320}
                maxWidth={GROVE_WIDE_MODAL_MAX_WIDTH_PX}
                maxHeight="80vh"
                flex={1}
                overflow="hidden"
            >
                <ScrollView flex={1} minHeight={0}>
                    <YStack gap={24} padding={24} paddingBottom={12}>
                        <H6Emphasized color="$gray1200">
                            Manual account details
                        </H6Emphasized>

                        <YStack gap={8}>
                            <LabelRegular color="$gray1000">
                                Account name
                            </LabelRegular>
                            <Input
                                size="regular"
                                value={name}
                                onChangeText={(t) => {
                                    setName(t);
                                    setValidationErrors((v) => ({
                                        ...v,
                                        name: undefined,
                                    }));
                                }}
                                disabled={inputsDisabled}
                            />
                            {validationErrors.name != null ? (
                                <BodySmallRegular color="$red900">
                                    {validationErrors.name}
                                </BodySmallRegular>
                            ) : null}
                        </YStack>

                        <YStack gap={8}>
                            <LabelRegular color="$gray1000">
                                Institution name
                            </LabelRegular>
                            <Input
                                size="regular"
                                value={institutionName}
                                onChangeText={(t) => {
                                    setInstitutionName(t);
                                    setValidationErrors((v) => ({
                                        ...v,
                                        institutionName: undefined,
                                    }));
                                }}
                                disabled={inputsDisabled}
                            />
                            {validationErrors.institutionName != null ? (
                                <BodySmallRegular color="$red900">
                                    {validationErrors.institutionName}
                                </BodySmallRegular>
                            ) : null}
                        </YStack>

                        {membership != null && ownerPickerOptions.length > 0 ? (
                            <YStack gap={8}>
                                <LabelRegular color="$gray1000">
                                    Account holder
                                </LabelRegular>
                                <Picker
                                    size="regular"
                                    value={selectedUserId}
                                    options={ownerPickerOptions}
                                    disabled={inputsDisabled}
                                    onChange={setSelectedUserId}
                                />
                            </YStack>
                        ) : null}

                        <YStack gap={8}>
                            <LabelRegular color="$gray1000">
                                Holistic category
                            </LabelRegular>
                            <Picker
                                size="regular"
                                value={holisticCategory}
                                options={categoryOptions}
                                disabled={inputsDisabled}
                                onChange={(v) => {
                                    setHolisticCategory(v);
                                }}
                            />
                        </YStack>

                        <YStack gap={8}>
                            <LabelRegular color="$gray1000">
                                Balance
                            </LabelRegular>
                            <DollarInput
                                size="regular"
                                value={amountStr}
                                onChangeText={setAmountStr}
                                allowDecimals={false}
                                disabled={inputsDisabled}
                            />
                        </YStack>

                        <YStack gap={8}>
                            <LabelRegular color="$gray1000">
                                Description
                            </LabelRegular>
                            <MultilineInput
                                value={description}
                                onChangeText={setDescription}
                                disabled={inputsDisabled}
                                numberOfLines={4}
                                placeholder="Description"
                            />
                        </YStack>

                        <YStack gap={8}>
                            <LabelRegular color="$gray1000">
                                Minimum payment
                            </LabelRegular>
                            <DollarInput
                                size="regular"
                                value={minimumPaymentStr}
                                onChangeText={setMinimumPaymentStr}
                                allowDecimals={false}
                                disabled={inputsDisabled}
                            />
                        </YStack>

                        <YStack gap={8}>
                            <LabelRegular color="$gray1000">
                                Interest rate (%)
                            </LabelRegular>
                            <Input
                                size="regular"
                                value={interestRateStr}
                                onChangeText={setInterestRateStr}
                                keyboardType="decimal-pad"
                                disabled={inputsDisabled}
                                placeholder="0"
                            />
                        </YStack>
                    </YStack>
                </ScrollView>

                <XStack
                    justifyContent="flex-end"
                    alignItems="center"
                    gap={12}
                    paddingHorizontal={24}
                    paddingVertical={16}
                    borderTopWidth={1}
                    borderTopColor="$gray200"
                    flexShrink={0}
                    backgroundColor="$white"
                >
                    {showSpinner ? <Spinner /> : null}
                    <Button
                        variant="tertiary"
                        disabled={inputsDisabled}
                        onPress={onClose}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        disabled={inputsDisabled}
                        onPress={() => {
                            void handleSave();
                        }}
                    >
                        Save
                    </Button>
                </XStack>
            </YStack>
        </GroveSimpleModal>
    );
};
